import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { debounce } from 'lodash';

import Strings from '../../Strings';
import Table3 from '../../containers/Table/Table3';
import { getProgramDisplayName } from '../../utils/cmsPrograms';
import { capTypesForRTM, getVitalsLabelsFromEnrollment } from '../../utils/enrollmentFields';
import { useGlobalPageSize } from '../../utils/tableUtils';
import { buildDirectOrderRouteState, sortEnrollmentsForDisplay } from './shippingRequestOrderState';
import { unlinkedHardwareTooltip } from './unlinkedHardware';
import { escapeHtml } from '../../utils';
import { openModalAction } from '../../actions/modal';
import { PageHeader } from '../../components/PageHeader';
import { TextInput } from '../../components/PageHeader/TextInput';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { getFiltersDefinition, OrgFilter } from '../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../components/AdvancedFilters/helpers';
import { renderShipmentAddress } from './ShipmentList';
import { GET_PATIENTS_WITHOUT_KIT_RESULT, shippingActions } from './actions';
import SimpleModal from '../../containers/Modal/SimpleModal';
import { Input } from '../../containers/Form';

const columnHelper = createColumnHelper();

// Informational only — it must never block Create Order.
function UnlinkedHardwareBadge({ unlinkedOrders }) {
  if (!unlinkedOrders?.length) return null;
  const summary = Strings.formatString(
    Strings.shipping.unlinkedHardwareWarning, unlinkedOrders.length,
  );
  return (
    <div
      className="signal-alert" 
      data-testid="unlinked-hardware-badge"
      data-tooltip-id="tooltip"
      data-tooltip-html={escapeHtml(unlinkedHardwareTooltip(unlinkedOrders)).replaceAll('\n', '<br />')}
      data-tooltip-place="bottom"
      style={{ marginLeft: 6, cursor: 'help', flexShrink: 0 }}
      role="img"
      aria-label={summary}
    />
  );
}

UnlinkedHardwareBadge.propTypes = { unlinkedOrders: PropTypes.array };

// The table cell ellipsizes its whole content, so a long name would truncate
// the badge away — exactly when the row is busiest. The name gets its own
// ellipsizing box and the badge is kept from shrinking.
function renderPatientNameCell(info) {
  return (
    <div className="icon-and-text-container">
      <div className="icon-and-text-content">
        {info.getValue() || '-'}
      </div>
      <UnlinkedHardwareBadge unlinkedOrders={info.row.original.unlinkedOrders} />
    </div>
  );
}

function SelectProgramsModal({ patient, onCancel, onSubmit }) {
  const programs = React.useMemo(() => sortEnrollmentsForDisplay(patient.patientEnrollment?.enrollments), [patient.patientEnrollment?.enrollments]);
  const [selectedPrograms, setSelectedPrograms] = React.useState([]);

  return (
    <SimpleModal
      name="programs-for-order-modal"
      title={Strings.shipping.createOrder}
      onCancel={onCancel}
      onSubmit={() => onSubmit(selectedPrograms)}
      isSubmitDisabled={selectedPrograms.length === 0}
      content={(
        <>
          <div className="label">{`${Strings.shipping.selectProgramsForOrder} (${Strings.shipping.oneKitPerOrder})`}</div>
          {programs.map(enrollment => (
            <Input
              type="checkbox"
              key={enrollment.id}
              id={`program-${enrollment.enrollmentUUID}`}
              name={`program-${enrollment.id}`}
              label={getProgramDisplayName(enrollment.program, enrollment.subprogram)}
              value={selectedPrograms.includes(enrollment.enrollmentUUID)}
              onChange={() => {
                setSelectedPrograms(prev => prev.includes(enrollment.enrollmentUUID)
                  ? prev.filter(id => id !== enrollment.enrollmentUUID)
                  : [...prev, enrollment.enrollmentUUID]
                );
              }}
              rowClassName="checkbox-row"
            />
          ))}
        </>
      )}
    />
  );
};

function ShippingRequests(props) {
  const { onNavigate, refreshTimestamp, orgs, filters, getPatientsWithoutKit } = props;
  const globalPageSize = useGlobalPageSize();
  const [patients, setPatients] = React.useState([]);
  const [pagination, setPagination] = React.useState({ offset: 0, limit: globalPageSize, totalRecords: 0 });
  const [loading, setLoading] = React.useState(false);
  const [showSelectProgramsModal, setShowSelectProgramsModal] = React.useState(false);
  const [selectedPatient, setSelectedPatient] = React.useState(null);

  const pageRequest = React.useRef({ offset: 0, limit: globalPageSize, excludeExistingOrdersAndDirectShipments: true });
  const requestSequence = React.useRef(0);

  const loadData = React.useCallback(async () => {
    requestSequence.current += 1;
    const sequence = requestSequence.current;
    setLoading(true);
    try {
      const resp = await getPatientsWithoutKit(pageRequest.current);
      if (sequence !== requestSequence.current) return;
      if (resp?.type === GET_PATIENTS_WITHOUT_KIT_RESULT) {
        setPatients(resp.response?.data || []);
        setPagination(resp.response?.pagination || { ...pageRequest.current, totalRecords: 0 });
      }
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [getPatientsWithoutKit]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('patientName', {
        cell: renderPatientNameCell,
        header: Strings.capPatient.patientName,
        size: 250,
      }),
      columnHelper.accessor('pharmacyPatientId', {
        cell: info => info.getValue() || '-',
        header: Strings.capPatient.patientId,
        size: 150,
      }),
      columnHelper.accessor('organizationName', {
        header: Strings.organization,
        accessorFn: row => orgs.find(org => org.masked_id === row.orgUUID)?.name || '-',
        size: 160,
      }),
      columnHelper.accessor('address', {
        header: Strings.capPatient.address,
        cell: info => info.getValue() ? renderShipmentAddress(info.getValue()) : '-',
        size: 420,
      }),
      columnHelper.accessor('textPhoneNo', {
        header: Strings.phoneNo,
        cell: info => info.getValue() || '-',
        size: 150,
      }),
      columnHelper.accessor('subprogram', {
        header: Strings.program,
        accessorFn: row => sortEnrollmentsForDisplay(row.patientEnrollment?.enrollments).map(enrollment =>
            getProgramDisplayName(
              enrollment?.program,
              enrollment?.subprogram,
            )
          ).join(', '),
        size: 160,
      }),
      columnHelper.accessor('vitals', {
        header: Strings.vitals,
        accessorFn: row => {
          const vitals = sortEnrollmentsForDisplay(row.patientEnrollment?.enrollments).map(enrollment =>
            getVitalsLabelsFromEnrollment(enrollment)?.join(', ') || '-'
          );
          return vitals.every(vital => vital === '-') ? '-' : vitals.join(', ');
        },
        size: 750,
      }),
      columnHelper.accessor('device_type', {
        header: Strings.deviceType,
        accessorFn: row => {
          const labels = sortEnrollmentsForDisplay(row.patientEnrollment?.enrollments).map(enrollment =>
            capTypesForRTM.find(c => c.value === enrollment?.rtmDeviceModel)?.label || enrollment?.rtmDeviceModel || '-'
          );
          return labels.every(label => label === '-') ? '-' : labels.join(', ');
        },
        size: 180,
      }),
    ],
    [orgs],
  );


  const onCreateOrder = patient => {
    const patientEnrollments = Object.values(patient.patientEnrollment?.enrollments);
    if (!patientEnrollments.length) return;
    if (patientEnrollments.length > 1) {
      setShowSelectProgramsModal(true);
      setSelectedPatient(patient);
      return;
    }
    onNavigate('/shipping/order-details/new', buildDirectOrderRouteState(patient));
  };

  const onSubmitEnrollments = selectedPrograms => {
    setShowSelectProgramsModal(false);
    if (!selectedPatient) return;
    onNavigate('/shipping/order-details/new', buildDirectOrderRouteState(selectedPatient, selectedPrograms));
    setSelectedPatient(null);
  };

  const buttons = React.useMemo(() => [
    {
      icon: 'register',
      onClick: (_, patient) => onCreateOrder(patient),
      text: Strings.shipping.createOrder,
    },
  ]);

  const onSearchQueryChange = React.useMemo(() => debounce(query => {
    pageRequest.current = { ...pageRequest.current, search: query, offset: 0 };
    loadData();
  }, 300), [loadData]);

  React.useEffect(() => () => onSearchQueryChange.cancel(), [onSearchQueryChange]);

  React.useEffect(() => {
    const appliedFilters = getRelevantFilters(filters);
    const orgId = appliedFilters.filter(filter => filter.group === 'orgId').map(filter => filter.value);
    const program = appliedFilters.find(filter => filter.group === 'program')?.value;
    pageRequest.current = {
      ...pageRequest.current,
      offset: 0,
      orgId: orgId.length ? orgId : undefined,
      program,
    };
    loadData();
  }, [filters, loadData, refreshTimestamp]);

  const onOffsetChange = offset => {
    pageRequest.current = { ...pageRequest.current, offset };
    loadData();
  };

  const onPageSizeChange = limit => {
    pageRequest.current = { ...pageRequest.current, offset: 0, limit };
    loadData();
  };

  const onSortingChange = sorting => {
    const next = sorting?.[0];
    const sortColumn = next?.id;
    const sortType = next ? (next.desc ? 'descending' : 'ascending') : undefined;
    if (pageRequest.current.sortColumn === sortColumn && pageRequest.current.sortType === sortType) return;
    pageRequest.current = { ...pageRequest.current, offset: 0, sortColumn, sortType };
    loadData();
  };

  return (
    <>
      <PageHeader
        right={(
          <>
            <TextInput placeholder={Strings.search} onChange={e => onSearchQueryChange(e.target.value)} />
            <AdvancedFilters>
              {orgs?.length > 0 && <Filter definition={OrgFilter(orgs).orgId} />}
              <Filter definition={getFiltersDefinition().program} />
            </AdvancedFilters>
          </>
        )}
      />
      <AdvancedFiltersBar customFilters={OrgFilter(orgs)} />
      <Table3
        data={patients}
        isLoading={loading}
        columns={columns}
        name="ShippingRequests"
        buttons={buttons}
        enableColumnFiltering
        enableRowSelection
        pinFirstColumn
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
        onSortingChange={onSortingChange}
      />
      {showSelectProgramsModal && (
        <SelectProgramsModal
          patient={selectedPatient}
          onCancel={() => setShowSelectProgramsModal(false)}
          onSubmit={onSubmitEnrollments}
        />
      )}
    </>
  );
}

ShippingRequests.propTypes = {
  onNavigate: PropTypes.func.isRequired,
  refreshTimestamp: PropTypes.number,
  orgs: PropTypes.array.isRequired,
  filters: PropTypes.array.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
  getPatientsWithoutKit: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  onNavigate: (path, state) => dispatch(push(path, state)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  getPatientsWithoutKit: pageRequest => dispatch(shippingActions.getPatientsWithoutKit(pageRequest)),
});

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ShippingRequests);
